// ─── ParameterConfigModal ────────────────────────────────────────────────────
// Port of the legacy Symfony "Parameter Settings" popup
// (Nexsens\ProjectBundle\Controller\DashBoardController::parameditformAction
// + saveParamSettingsAction).
//
// Two pickers at the top — Site, then Logger (Device). Once a device is
// selected, the body lists every sensor on that device with a row per
// parameter. Each row exposes:
//
//   Status  (Show | Hide | Meta)
//   Name
//   Unit          (display label, read-only — set by ingest)
//   Convert       (display_unit dropdown, restricted to compatible units)
//   Resolution    (precision 0..6)
//   Sensor min    (ymin)
//   Sensor max    (ymax)
//   Filter        (filter_dashboard_graph)
//   Map           (show_map_tooltip — column labelled "Map" per XD)
//
// Save POSTs a batched payload to /api/data/parameter-config/save/. Validation
// runs server-side (ymin ≤ ymax, precision 0..6, parameter belongs to device).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { X, Loader, AlertCircle, CheckCircle } from 'lucide-react'
import { projectsApi } from '../../lib/projectsApi'

const STATUS_OPTIONS = [
    { value: 'show', label: 'Show' },
    { value: 'hide', label: 'Hide' },
]
// Each entry is the number of decimal places; label shows the example value at that precision.
const PRECISION_OPTIONS = [
    { value: 0, label: '0' },
    { value: 1, label: '0.0' },
    { value: 2, label: '0.00' },
    { value: 3, label: '0.000' },
    { value: 4, label: '0.0000' },
    { value: 5, label: '0.00000' },
    { value: 6, label: '0.000000' },
]

// ── Sensor min/max unit handling ───────────────────────────────────────────
// ymin/ymax are STORED in the parameter's own (original) unit, but the modal
// SHOWS and ACCEPTS them in whatever Convert (display) unit is selected — e.g.
// converting Air Temperature °C→°F, Sensor min/max read in °F. The API supplies
// per-unit factors (converted = value*slope + intercept) so these conversions
// stay client-side and the stored value never changes unit.
const convFactors = (p, unitId) => {
    const m = p?.unit_conversions || {}
    return m[String(unitId)] || { slope: 1, intercept: 0 }
}
// Unit the on-screen Sensor min/max values are currently expressed in.
const selectedUnitId = (p) => (p?.convert_unit_id ?? p?.orig_unit_id)
// original-unit value → display-unit value (blank stays blank)
const toDisplayUnit = (p, vOrig, unitId = selectedUnitId(p)) => {
    if (vOrig === null || vOrig === undefined || vOrig === '') return ''
    const n = Number(vOrig)
    if (!Number.isFinite(n)) return ''
    const { slope, intercept } = convFactors(p, unitId)
    return Number((n * slope + intercept).toFixed(6))
}
// display-unit value → original-unit value (blank stays null)
const toOriginalUnit = (p, vDisp, unitId = selectedUnitId(p)) => {
    if (vDisp === null || vDisp === undefined || vDisp === '') return null
    const n = Number(vDisp)
    if (!Number.isFinite(n)) return null
    const { slope, intercept } = convFactors(p, unitId)
    if (!slope) return n
    return Number(((n - intercept) / slope).toFixed(6))
}
// Short label for the unit the Sensor min/max fields are currently in, so the
// user can see (and trust) which unit they're editing.
const selectedUnitLabel = (p) => {
    const sel = selectedUnitId(p)
    if (sel != null && String(sel) !== String(p?.orig_unit_id)) {
        const u = (p?.available_units || []).find((x) => String(x.id) === String(sel))
        if (u) return u.short_name || u.long_name || ''
    }
    return p?.orig_unit_name || p?.unit_name || ''
}

export function ParameterConfigModal({ open, onClose, onSaved, sites = [], initialSiteId = null, initialDeviceId = null }) {
    // Picker state
    const [siteId,   setSiteId]   = useState(initialSiteId)
    const [deviceId, setDeviceId] = useState(initialDeviceId)

    // Data state — sensors + parameters for the chosen device
    const [sensors,       setSensors]       = useState([])
    const [dataRangeMode, setDataRangeMode] = useState('last') // 'current' | 'last'
    const [loading,  setLoading]  = useState(false)
    const [error,    setError]    = useState(null)
    const [dirty,       setDirty]       = useState(false)
    const [saving,      setSaving]      = useState(false)
    const [saveError,   setSaveError]   = useState(null)
    const [saveSuccess, setSaveSuccess] = useState(null) // null | { updated, skipped }
    const closeTimerRef = useRef(null)

    // ── Reset whenever the modal opens ──────────────────────────────────
    useEffect(() => {
        if (!open) return
        setSiteId(initialSiteId)
        setDeviceId(initialDeviceId)
        setSensors([])
        setDirty(false)
        setError(null)
        setSaveError(null)
        setSaveSuccess(null)
        if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null }
    }, [open, initialSiteId, initialDeviceId])

    // ── Escape closes the modal (with dirty-guard) ──────────────────────
    useEffect(() => {
        if (!open) return
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (dirty && !confirm('Discard unsaved parameter changes?')) return
                onClose?.()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, dirty, onClose])

    // ── Derive devices for the chosen site ──────────────────────────────
    const devices = useMemo(() => {
        const site = sites.find((s) => String(s.id) === String(siteId))
        return site?.devices || []
    }, [sites, siteId])

    // ── Load parameters whenever device changes ─────────────────────────
    useEffect(() => {
        if (!open || !deviceId) { setSensors([]); return }
        let cancelled = false
        setLoading(true); setError(null); setDirty(false)
        projectsApi.fetchParameterConfig(deviceId)
            .then((res) => {
                if (!cancelled) {
                    // 'Meta' status was removed from the UI — surface any
                    // existing meta parameters as 'Show' so the dropdown has a
                    // matching option (they're visible params either way).
                    const sensors = (res.sensors || []).map((s) => ({
                        ...s,
                        parameters: (s.parameters || []).map((p0) => {
                            // 'Meta' status was removed from the UI — surface as 'Show'.
                            const p = p0.status === 'meta' ? { ...p0, status: 'show' } : { ...p0 }
                            // Show Sensor min/max in the selected Convert unit
                            // (stored in the original unit; converted back on save).
                            p.ymin = toDisplayUnit(p, p.ymin)
                            p.ymax = toDisplayUnit(p, p.ymax)
                            return p
                        }),
                    }))
                    setSensors(sensors)
                    setDataRangeMode(res.device?.data_range_mode || 'last')
                }
            })
            .catch((e) => {
                if (cancelled) return
                const msg = e?.response?.data?.message || 'Could not load parameter configuration.'
                setError(msg)
                setSensors([])
            })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [open, deviceId])

    // ── Field updaters (mark dirty on every change) ─────────────────────
    const updateParam = useCallback((sensorIdx, paramIdx, field, value) => {
        setSensors((prev) => prev.map((s, si) => {
            if (si !== sensorIdx) return s
            return {
                ...s,
                parameters: s.parameters.map((p, pi) => {
                    if (pi !== paramIdx) return p
                    return { ...p, [field]: value }
                }),
            }
        }))
        setDirty(true)
    }, [])

    // Changing the Convert unit re-expresses the on-screen Sensor min/max in the
    // newly selected unit (via the original unit), so the numbers keep meaning
    // the same physical reading rather than silently switching interpretation.
    const updateConvert = useCallback((sensorIdx, paramIdx, newConvertId) => {
        setSensors((prev) => prev.map((s, si) => {
            if (si !== sensorIdx) return s
            return {
                ...s,
                parameters: s.parameters.map((p, pi) => {
                    if (pi !== paramIdx) return p
                    const oldUnit = selectedUnitId(p)
                    const newUnit = newConvertId ?? p.orig_unit_id
                    const reexpr = (vDisp) => toDisplayUnit(p, toOriginalUnit(p, vDisp, oldUnit), newUnit)
                    return { ...p, convert_unit_id: newConvertId, ymin: reexpr(p.ymin), ymax: reexpr(p.ymax) }
                }),
            }
        }))
        setDirty(true)
    }, [])

    const updateSensorHidden = useCallback((sensorIdx, value) => {
        setSensors((prev) => prev.map((s, si) => (si === sensorIdx ? { ...s, hidden: !!value } : s)))
        setDirty(true)
    }, [])

    // ── Save handler ────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        if (!deviceId) return
        const payload = {
            device_id: Number(deviceId),
            data_range_mode: dataRangeMode,
            sensors: sensors.map((s) => ({ id: s.id, hidden: s.hidden ? 1 : 0 })),
            params: sensors.flatMap((s) => s.parameters.map((p) => ({
                id: p.id,
                status: p.status,
                name: p.name || '',
                convert: p.convert_unit_id ?? null,
                precision: Number(p.precision ?? 0),
                // Sensor min/max are edited in the display unit — convert back to
                // the parameter's original unit (how they're stored) for save.
                ymin: toOriginalUnit(p, p.ymin),
                ymax: toOriginalUnit(p, p.ymax),
                filter_dashboard_graph: p.filter_dashboard_graph ? 1 : 0,
                show_diagnostic_graph:  p.show_diagnostic_graph  ? 1 : 0,
                show_map_tooltip:       p.show_map_tooltip       ? 1 : 0,
            }))),
        }
        setSaving(true); setSaveError(null); setSaveSuccess(null)
        try {
            const result = await projectsApi.saveParameterConfig(payload)
            setDirty(false)
            const skipped = result?.skipped_param_ids || []
            setSaveSuccess({
                updated: result?.updated_params ?? '?',
                skipped: skipped.length,
            })
            // Fire immediately (not gated on the 2.5s auto-close below) so
            // widgets can start re-fetching with the new config right away —
            // e.g. LineChartWidget re-requests graph data when a parameter's
            // unit Convert / Sensor min-max / precision just changed here.
            onSaved?.()
            // Auto-close after 2.5 s so the user can read the confirmation.
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
            closeTimerRef.current = setTimeout(() => {
                closeTimerRef.current = null
                onClose?.()
            }, 2500)
        } catch (e) {
            // Extract as much detail as possible for easier debugging.
            const res  = e?.response
            const data = res?.data
            let msg = 'Save failed — please try again.'
            if (data?.details) {
                msg = `${data.message || 'Validation failed'}: ${data.details.join(' · ')}`
            } else if (data?.message) {
                msg = data.message
            } else if (res?.status === 401 || res?.status === 403) {
                msg = 'Session expired — please refresh the page and try again.'
            } else if (res?.status >= 500) {
                msg = data?.message
                    ? `Server error: ${data.message}`
                    : 'Server error — please contact support if this continues.'
            } else if (!res) {
                msg = 'Network error — check your connection and try again.'
            }
            console.error('[ParameterConfigModal] save failed:', {
                status: res?.status,
                data,
                message: e?.message,
            })
            setSaveError(msg)
        } finally {
            setSaving(false)
        }
    }, [deviceId, sensors, dataRangeMode, onClose])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">

                {/* Header */}
                <div className="break-mb flex items-center justify-between px-6 py-3 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-[#4D4D4D]">Parameter Configuration</h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!deviceId || saving || !dirty}
                            className="px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => { if (dirty && !confirm('Discard unsaved parameter changes?')) return; onClose?.() }}
                            disabled={saving}
                            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >Cancel</button>
                        <button
                            type="button"
                            onClick={() => { if (dirty && !confirm('Discard unsaved parameter changes?')) return; onClose?.() }}
                            disabled={saving}
                            className="cross-bt p-1 text-gray-400 hover:text-gray-700 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Pickers */}
                <div className="px-6 py-3 border-b border-gray-100 grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Select Site</label>
                        <select
                            value={siteId || ''}
                            onChange={(e) => { setSiteId(e.target.value ? Number(e.target.value) : null); setDeviceId(null) }}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
                        >
                            <option value="">Select Site…</option>
                            {sites.map((s) => (
                                <option key={s.id} value={s.id}>{s.name || s.station_name || `Site ${s.id}`}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Select Logger</label>
                        <select
                            value={deviceId || ''}
                            onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : null)}
                            disabled={!siteId || devices.length === 0}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
                        >
                            <option value="">{!siteId ? 'Select a site first…' : (devices.length === 0 ? 'No devices on this site' : 'Select Logger…')}</option>
                            {devices.map((d) => (
                                <option key={d.id} value={d.id}>{d.name || d.serial_number || `Device ${d.id}`}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Data Range — device-level, mirrors Symfony "Current Period / Last 24 Hrs." */}
                {deviceId && !loading && !error && (
                    <div className="px-6 py-3 border-b border-gray-100">
                        <p className="text-xs font-semibold text-[#4D4D4D] mb-2">Data Range</p>
                        <label className="flex items-center gap-2 text-sm text-gray-700 mb-1 cursor-pointer">
                            <input
                                type="radio" name="data_range_mode" value="current"
                                checked={dataRangeMode === 'current'}
                                onChange={() => { setDataRangeMode('current'); setDirty(true) }}
                                className="accent-[#358ECD]"
                            />
                            Current Period (i.e. Today)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                                type="radio" name="data_range_mode" value="last"
                                checked={dataRangeMode === 'last'}
                                onChange={() => { setDataRangeMode('last'); setDirty(true) }}
                                className="accent-[#358ECD]"
                            />
                            Last Period (i.e. Last 24 Hrs.)
                        </label>
                    </div>
                )}

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-4">
                    {/* No "Successfully saved …" confirmation on save — only
                        surface a banner when some parameters were skipped. */}
                    {saveSuccess && saveSuccess.skipped > 0 && (
                        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded text-xs flex items-start gap-2">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>
                                <span className="font-semibold">{saveSuccess.skipped} parameter{saveSuccess.skipped !== 1 ? 's' : ''} could not be saved</span> (invalid unit conversion).
                            </span>
                        </div>
                    )}
                    {saveError && (
                        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs flex items-start gap-2">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{saveError}</span>
                        </div>
                    )}

                    {!deviceId ? (
                        <div className="h-48 flex flex-col items-center justify-center text-sm text-gray-500 gap-2">
                            <p>Pick a site and logger above to configure its parameters.</p>
                        </div>
                    ) : loading ? (
                        <div className="h-48 flex items-center justify-center">
                            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
                        </div>
                    ) : error ? (
                        <div className="h-48 flex items-center justify-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">{error}</div>
                    ) : sensors.length === 0 ? (
                        <div className="h-48 flex items-center justify-center text-sm text-gray-400">No sensors on this device.</div>
                    ) : (
                        <h3 className="text-sm font-semibold text-[#4D4D4D] mb-3">Sensor and Parameter</h3>
                    )}

                    {!loading && !error && deviceId && sensors.map((sensor, si) => (
                        <div key={sensor.id} className="mb-5">
                            <div className="flex items-center gap-2 mb-2">
                                <input
                                    type="checkbox"
                                    checked={!sensor.hidden}
                                    onChange={(e) => updateSensorHidden(si, !e.target.checked)}
                                    title="Show / hide entire sensor"
                                    className="w-4 h-4 accent-[#358ECD]"
                                />
                                <h4 className="text-sm font-semibold text-gray-800">
                                    {sensor.name || `Sensor ${sensor.id}`}
                                    <span className="ml-2 text-xs font-normal text-gray-400">[{sensor.parameters.length}]</span>
                                </h4>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600">
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 100 }}>Status</th>
                                            <th className="text-left px-2 py-2 font-semibold">Parameter</th>
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 70 }}>Unit</th>
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 120 }}>Convert</th>
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 90 }}>Precision</th>
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 100 }}>Sensor min</th>
                                            <th className="text-left px-2 py-2 font-semibold" style={{ width: 100 }}>Sensor max</th>
                                            <th className="text-center px-2 py-2 font-semibold" style={{ width: 60 }}>Filter</th>
                                            <th className="text-center px-2 py-2 font-semibold" style={{ width: 60 }}>Map</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sensor.parameters.map((p, pi) => (
                                            <tr key={p.id} className="border-t border-gray-100">
                                                <td className="px-2 py-1.5">
                                                    <select
                                                        value={p.status}
                                                        onChange={(e) => updateParam(si, pi, 'status', e.target.value)}
                                                        className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:border-[#358ECD]"
                                                    >
                                                        {STATUS_OPTIONS.map((o) => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        type="text" value={p.name || ''}
                                                        placeholder={p.orig_name || ''}
                                                        onChange={(e) => updateParam(si, pi, 'name', e.target.value)}
                                                        className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-[#358ECD]"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5 text-gray-700">
                                                    <span className="inline-block px-2 py-0.5 text-[11px] bg-gray-100 rounded">{p.unit_name || '—'}</span>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    {/* Convertible = available units minus the parameter's
                                                        OWN unit (converting to the same unit is a no-op that
                                                        "— (no convert)" already covers). Compare against the
                                                        original unit, falling back to the current unit only
                                                        when nothing is converted, so a selected converted
                                                        unit is never removed. When nothing is convertible,
                                                        show a plain "—" instead of an empty dropdown. */}
                                                    {(() => {
                                                        const selfUnit = p.orig_unit_name || (p.convert_unit_id == null ? p.unit_name : '')
                                                        const convertible = (p.available_units || []).filter(
                                                            (u) => (u.short_name || u.long_name) !== selfUnit
                                                        )
                                                        if (convertible.length === 0) {
                                                            return <span className="text-gray-400">—</span>
                                                        }
                                                        return (
                                                            <select
                                                                value={p.convert_unit_id ?? ''}
                                                                onChange={(e) => updateConvert(si, pi, e.target.value ? Number(e.target.value) : null)}
                                                                className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:border-[#358ECD]"
                                                            >
                                                                <option value="">— (no convert)</option>
                                                                {convertible.map((u) => {
                                                                    const uLabel = u.short_name || u.long_name
                                                                    const label = p.orig_unit_name && p.orig_unit_name !== uLabel
                                                                        ? `${p.orig_unit_name} → ${uLabel}`
                                                                        : uLabel
                                                                    return <option key={u.id} value={u.id}>{label}</option>
                                                                })}
                                                            </select>
                                                        )
                                                    })()}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <select
                                                        value={p.precision ?? 0}
                                                        onChange={(e) => updateParam(si, pi, 'precision', Number(e.target.value))}
                                                        className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:border-[#358ECD]"
                                                    >
                                                        {PRECISION_OPTIONS.map(({ value, label }) => (
                                                            <option key={value} value={value}>{label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="number" inputMode="decimal" step="any"
                                                            value={p.ymin ?? ''}
                                                            onChange={(e) => updateParam(si, pi, 'ymin', e.target.value)}
                                                            className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-[#358ECD]"
                                                        />
                                                        {selectedUnitLabel(p) && (
                                                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{selectedUnitLabel(p)}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="number" inputMode="decimal" step="any"
                                                            value={p.ymax ?? ''}
                                                            onChange={(e) => updateParam(si, pi, 'ymax', e.target.value)}
                                                            className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-[#358ECD]"
                                                        />
                                                        {selectedUnitLabel(p) && (
                                                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{selectedUnitLabel(p)}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!p.filter_dashboard_graph}
                                                        onChange={(e) => updateParam(si, pi, 'filter_dashboard_graph', e.target.checked)}
                                                        className="w-4 h-4 accent-[#358ECD]"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!p.show_map_tooltip}
                                                        onChange={(e) => updateParam(si, pi, 'show_map_tooltip', e.target.checked)}
                                                        className="w-4 h-4 accent-[#358ECD]"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
